import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Card, Form, Input, Button, Table, Space, Typography, Divider,
  Modal, message, Row, Col, Statistic, Alert, InputNumber, AutoComplete,
  Select
} from 'antd';
import {
  ShoppingCartOutlined, DeleteOutlined, PlusOutlined, MinusOutlined,
  UserOutlined, PhoneOutlined, FileTextOutlined, DollarOutlined,
  CreditCardOutlined, GlobalOutlined, PlusCircleOutlined, UserAddOutlined
} from '@ant-design/icons';
import apiClient from '../config/axios';
import { ENV_CONFIG } from '../config/environment';
import PatientService, { PatientCreateData } from '../services/patientService';
import { getCurrentISTISO, formatForReceipt } from '../utils/timezone';

const { Title } = Typography;
const { TextArea } = Input;

interface Patient {
  id: number;
  user_id: string;
  name: string;
  phone_number: string;
  email?: string;
  age?: number;
  gender?: string;
  created_at: string;
  updated_at: string;
}

interface CartItem {
  medicine: {
    branch_medicine_id: number;
    medicine_name: string;
    manufacturer: string;
    unit_price: number;
    hike_percent: number;
    quantity_available?: number;
  };
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface PaymentMethod {
  id: string;
  method: 'cash' | 'card' | 'upi';
  amount: number;
}

interface CheckoutFormProps {
  cartItems: CartItem[];
  onUpdateCart: (items: CartItem[]) => void;
  onClearCart: () => void;
  onCheckoutSuccess?: () => void;
  branchId?: number;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  cartItems,
  onUpdateCart,
  onClearCart,
  onCheckoutSuccess,
  branchId
}) => {
  const [form] = Form.useForm();
  const [createPatientForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [patientOptions, setPatientOptions] = useState<{ value: string; label: string; patient: Patient; key: string }[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [createPatientModalVisible, setCreatePatientModalVisible] = useState(false);
  const [logoBase64, setLogoBase64] = useState<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  
  // Medicine discount and consultation fee state
  const [medicineDiscountPercentage, setMedicineDiscountPercentage] = useState<number>(0);
  // const [consultationFee, setConsultationFee] = useState<number>(0); // Commented out - not needed for now
  const consultationFee = 0; // Hardcoded to 0 - consultation fee feature disabled
  
  // Load logo as base64 on component mount
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/Gosval.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    };
    loadLogo();
  }, []);

  // Calculate functions
  const calculateSubtotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + item.totalPrice, 0);
  }, [cartItems]);

  const calculateTax = useCallback(() => {
    // No tax applied
    return 0;
  }, []);

  const calculateMedicineDiscountAmount = useCallback(() => {
    const subtotal = calculateSubtotal();
    const discountAmount = (subtotal * medicineDiscountPercentage) / 100;
    return Math.min(discountAmount, subtotal); // Cannot exceed medicine subtotal
  }, [calculateSubtotal, medicineDiscountPercentage]);

  const calculateDiscountedMedicineTotal = useCallback(() => {
    const subtotal = calculateSubtotal();
    const discount = calculateMedicineDiscountAmount();
    return Math.max(0, subtotal - discount);
  }, [calculateSubtotal, calculateMedicineDiscountAmount]);

  const calculateTotal = useCallback(() => {
    const discountedMedicine = calculateDiscountedMedicineTotal();
    const tax = calculateTax();
    // return discountedMedicine + tax + consultationFee; // Commented out - consultancy fee not needed
    return discountedMedicine + tax; // No consultation fee
  }, [calculateDiscountedMedicineTotal, calculateTax]);

  // Auto-add default payment method when cart has items
  useEffect(() => {
    if (cartItems.length > 0 && paymentMethods.length === 0) {
      const total = calculateTotal();
      const defaultPayment: PaymentMethod = {
        id: Date.now().toString(),
        method: 'cash',
        amount: total
      };
      setPaymentMethods([defaultPayment]);
    }
  }, [cartItems.length, paymentMethods.length, calculateTotal]);

  // Update payment amount when total changes (if only one payment method exists)
  useEffect(() => {
    if (paymentMethods.length === 1 && cartItems.length > 0) {
      setPaymentMethods(prev => prev.map(payment => ({
        ...payment,
        amount: calculateTotal()
      })));
    }
  }, [cartItems, paymentMethods.length, calculateTotal]);

  // Patient search functions
  const searchPatientsByName = useCallback(async (name: string) => {
    if (!name || name.length < 2) {
      setPatientOptions([]);
      return;
    }

    setSearchingPatients(true);
    try {
      const response = await apiClient.get('/patients/search', {
        params: {
          name: name,
          page: 1,
          limit: 10,
          exact: false
        }
      });

      if (response.data.success && response.data.data) {
        const options = response.data.data.map((patient: any, index: number) => ({
          value: patient.attributes?.name || patient.name,
          label: `${patient.attributes?.name || patient.name} (${patient.attributes?.phone_number || patient.phone_number})`,
          patient: {
            id: patient.id,
            user_id: patient.attributes?.user_id || patient.user_id,
            name: patient.attributes?.name || patient.name,
            phone_number: patient.attributes?.phone_number || patient.phone_number,
            client: patient.attributes?.client || patient.client
          },
          key: `name-${patient.id || index}` // Add unique key
        }));
        setPatientOptions(options);
      }
    } catch (error) {
      console.error('Error searching patients:', error);
      message.error('Failed to search patients');
    } finally {
      setSearchingPatients(false);
    }
  }, []);

  const searchPatientsByPhone = useCallback(async (phone: string) => {
    if (!phone || phone.length < 3) {
      setPatientOptions([]);
      return;
    }

    setSearchingPatients(true);
    try {
      const response = await apiClient.get('/patients/search', {
        params: {
          phone: phone,
          page: 1,
          limit: 10,
          exact: false
        }
      });

      if (response.data.success && response.data.data) {
        const options = response.data.data.map((patient: any, index: number) => ({
          value: patient.attributes?.phone_number || patient.phone_number,
          label: `${patient.attributes?.name || patient.name} (${patient.attributes?.phone_number || patient.phone_number})`,
          patient: {
            id: patient.id,
            user_id: patient.attributes?.user_id || patient.user_id,
            name: patient.attributes?.name || patient.name,
            phone_number: patient.attributes?.phone_number || patient.phone_number,
            client: patient.attributes?.client || patient.client
          },
          key: `phone-${patient.id || index}` // Add unique key
        }));
        setPatientOptions(options);
      } else {
        setPatientOptions([]);
      }
    } catch (error) {
      console.error('Error searching patients by phone:', error);
      message.error('Failed to search patients by phone');
    } finally {
      setSearchingPatients(false);
    }
  }, []);

  const handlePatientSelect = useCallback((value: string, option: any) => {
    if (option && option.patient) {
      const patient = option.patient;
      // Clear the options to prevent interference
      setPatientOptions([]);
      // Set both fields with the patient data
      form.setFieldsValue({
        customer_name: patient.name,
        customer_mobile: patient.phone_number.replace('+', '')
      });
    }
  }, [form]);

  const handleCreatePatient = useCallback(async (values: PatientCreateData) => {
    try {
      const patient = await PatientService.findOrCreatePatient(values);
      
      // Set both fields with the created/found patient data
      form.setFieldsValue({
        customer_name: patient.name,
        customer_mobile: patient.phoneNumber
      });
      
      message.success(patient.created ? 'Patient created successfully' : 'Patient found and selected');
      setCreatePatientModalVisible(false);
      createPatientForm.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Failed to create/find patient');
    }
  }, [form, createPatientForm]);

  // Debounced search functions
  const debouncedSearchByName = useCallback((name: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      searchPatientsByName(name);
    }, 300);
  }, [searchPatientsByName]);

  const debouncedSearchByPhone = useCallback((phone: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      searchPatientsByPhone(phone);
    }, 300);
  }, [searchPatientsByPhone]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const updateQuantity = (branchMedicineId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      onUpdateCart(cartItems.filter(item => item.medicine.branch_medicine_id !== branchMedicineId));
    } else {
      const updatedItems = cartItems.map(item => {
        if (item.medicine.branch_medicine_id === branchMedicineId) {
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: item.unitPrice * newQuantity
          };
        }
        return item;
      });
      onUpdateCart(updatedItems);
    }
  };

  const removeItem = (branchMedicineId: number) => {
    onUpdateCart(cartItems.filter(item => item.medicine.branch_medicine_id !== branchMedicineId));
  };

  // Payment method management functions
  const addPaymentMethod = () => {
    const newPayment: PaymentMethod = {
      id: Date.now().toString(),
      method: 'cash',
      amount: 0
    };
    setPaymentMethods([...paymentMethods, newPayment]);
  };

  const updatePaymentMethod = (id: string, field: keyof PaymentMethod, value: any) => {
    setPaymentMethods(paymentMethods.map(payment => 
      payment.id === id ? { ...payment, [field]: value } : payment
    ));
  };

  const removePaymentMethod = (id: string) => {
    setPaymentMethods(paymentMethods.filter(payment => payment.id !== id));
  };

  const getTotalPaid = () => {
    return paymentMethods.reduce((total, payment) => total + payment.amount, 0);
  };

  const getRemainingAmount = () => {
    return calculateTotal() - getTotalPaid();
  };

  const isPaymentComplete = () => {
    return Math.abs(getRemainingAmount()) < ENV_CONFIG.PAYMENT_TOLERANCE;
  };

  const handleCheckout = async (values: any) => {
    console.log('handleCheckout called with:', { 
      cartItemsLength: cartItems.length, 
      paymentMethodsLength: paymentMethods.length,
      paymentMethods,
      total: calculateTotal(),
      totalPaid: getTotalPaid(),
      remaining: getRemainingAmount(),
      isComplete: isPaymentComplete()
    });

    if (cartItems.length === 0) {
      message.error('Cart is empty');
      return;
    }

    if (paymentMethods.length === 0) {
      message.error('Please add at least one payment method');
      return;
    }

    const remainingAmount = getRemainingAmount();
    const totalAmount = calculateTotal();
    const totalPaid = getTotalPaid();
    
    if (remainingAmount > 0) {
      message.error(`Payment incomplete. Remaining amount: ₹${remainingAmount.toFixed(2)}`);
      return;
    }
    
    if (remainingAmount < 0) {
      const overpaidAmount = Math.abs(remainingAmount);
      message.warning(`Overpaid by ₹${overpaidAmount.toFixed(2)}. Change to be returned: ₹${overpaidAmount.toFixed(2)}`);
    } else if (remainingAmount === 0) {
      message.success('Payment completed successfully!');
    }

    // Calculate totals
    const medicineSubtotal = calculateSubtotal();
    const medicineDiscountAmount = calculateMedicineDiscountAmount();

    // Generate unique receipt number
    const receiptNumber = `${ENV_CONFIG.RECEIPT_PREFIX}${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;

    const checkoutData = {
      branch: branchId,
      patient_name: values.customer_name,
      patient_id: values.customer_mobile, // Using mobile as patient ID for now
      sub_total: medicineSubtotal, // Medicine subtotal before discount
      discount_amount: medicineDiscountAmount, // Medicine discount
      total_amount: totalAmount, // Final total (discounted medicine only - consultation fee disabled)
      tax_amount: 0,
      payment_status: "paid", // Since we're processing payment
      notes: values.notes || '',
      receipt_number: receiptNumber,
      // Include payment methods data
      payment_methods: paymentMethods.map(payment => ({
        payment_method: payment.method,
        amount: payment.amount,
        payment_date: getCurrentISTISO()
      })),
      // Note: sale_items and sale_payments will be created separately
      // as they require additional API calls to create the items first
    };

    console.log('Checkout data being sent:', checkoutData);

    setLoading(true);
    try {
      // First, create the sale
      const response = await apiClient.post('/sales', checkoutData);

      console.log('Sale creation response:', response.data);
      console.log('Checking condition - status:', response.data.status, 'success:', response.data.success);
      
      // Check if sale was created successfully (handle different response formats)
      const isSuccess = response.data.status === 'success' || 
                       response.data.success === true || 
                       response.status === 201 || 
                       (response.data.data && response.data.data.id);
      
      if (isSuccess) {
        const saleId = response.data.data.id;
        console.log('Sale created successfully with ID:', saleId);
        
        // Then, create sale items if there are cart items
        console.log('Checking cart items length:', cartItems.length);
        console.log('Cart items:', cartItems);
        if (cartItems.length > 0) {
          console.log('Entering sale items creation block');
            try {
            // Prepare sale items data using branch_medicine_id directly from cart items
            const saleItemsData = cartItems
              .map(item => {
                const branchMedicineId = item.medicine.branch_medicine_id;
                if (!branchMedicineId) {
                  console.error(`Cart item missing branch_medicine_id for medicine ${item.medicine.medicine_name}`);
                  return null;
                }
                return {
                  branch_medicine: branchMedicineId,
                  quantity: item.quantity,
                  unit_price: item.unitPrice,
                  cost_price: item.unitPrice * ENV_CONFIG.COST_PRICE_MARKUP,
                  discount_percentage: 0
                };
              })
              .filter(item => item !== null);
            
            if (saleItemsData.length === 0) {
              throw new Error('No valid items found to create sale items');
            }
            
            console.log(`Preparing to create ${saleItemsData.length} sale items out of ${cartItems.length} cart items`);
            
            console.log('Preparing to create sale items:', saleItemsData);
            
            // Create sale items
            const saleItemsResponse = await apiClient.post(`/sale-items/sale/${saleId}/bulk`, {
              items: saleItemsData
            });
            
            console.log('Sale items creation response:', saleItemsResponse.data);
            console.log('Sale items created successfully');
            
            // Create sale payments if there are payment methods
            if (paymentMethods.length > 0) {
              console.log('Creating sale payments:', paymentMethods);
              try {
                const salePaymentsData = paymentMethods.map(payment => ({
                  sale: saleId,
                  payment_method: payment.method,
                  amount: payment.amount,
                  payment_date: getCurrentISTISO(),
                  notes: ''
                }));
                
                console.log('Sale payments data:', salePaymentsData);
                
                const salePaymentsResponse = await apiClient.post('/sale-payments/bulk', {
                  sale_payments: salePaymentsData
                });
                
                console.log('Sale payments creation response:', salePaymentsResponse.data);
                console.log('Sale payments created successfully');
              } catch (salePaymentsError: any) {
                console.error('Error creating sale payments:', salePaymentsError);
                console.error('Sale payments error details:', salePaymentsError.response?.data);
                message.warning('Sale completed but payment methods could not be saved. Please contact support.');
              }
            } else {
              console.log('No payment methods to create');
            }
            
            // Update branch medicine stock for each sold item (compute new stock from cart snapshot)
            console.log('Starting stock update process using cart snapshot...');
            try {
              let successfulUpdates = 0;
              let failedUpdates = 0;
              
              // Process stock updates sequentially to avoid potential build issues
              for (let index = 0; index < saleItemsData.length; index++) {
                const saleItem = saleItemsData[index] as any;
                if (!saleItem) continue; // Skip null items
                try {
                  console.log(`Updating stock for branch medicine ${saleItem.branch_medicine}, reducing by ${saleItem.quantity}`);
                  // Use the cart item's initial stock snapshot if available
                  const cartItem = cartItems[index];
                  const snapshotStock = cartItem?.medicine?.quantity_available ?? 0;
                  const newStock = Math.max(0, snapshotStock - saleItem.quantity);
                  console.log(`Branch medicine ${saleItem.branch_medicine}: snapshot stock ${snapshotStock}, sold ${saleItem.quantity}, new stock ${newStock}`);
                  
                  // Update the branch medicine stock
                  const updateResponse = await apiClient.put(`/branch-medicines/${saleItem.branch_medicine}`, {
                    current_stock: newStock,
                    last_sold_date: getCurrentISTISO()
                  });
                  
                  console.log(`Successfully updated stock for branch medicine ${saleItem.branch_medicine}`);
                  successfulUpdates++;
                } catch (stockUpdateError: any) {
                  console.error(`Error updating stock for branch medicine ${saleItem.branch_medicine}:`, stockUpdateError);
                  console.error('Stock update error details:', stockUpdateError.response?.data);
                  failedUpdates++;
                }
              }
              
              console.log(`Stock update completed: ${successfulUpdates} successful, ${failedUpdates} failed`);
              
              if (failedUpdates > 0) {
                message.warning(`Stock updated for ${successfulUpdates} items, but ${failedUpdates} items failed to update. Please check inventory manually.`);
              } else {
                console.log('All stock updates completed successfully');
              }
              
            } catch (stockUpdateError: any) {
              console.error('Error in stock update process:', stockUpdateError);
              message.warning('Sale completed but stock update failed. Please update inventory manually.');
            }
            
                // Wait a bit for the database to update before fetching
                await new Promise(resolve => setTimeout(resolve, ENV_CONFIG.DATABASE_UPDATE_DELAY));
            
            // Fetch the created sale items to include in receipt
            try {
              const fetchedSaleItemsResponse = await apiClient.get(`/sale-items/sale/${saleId}`);
              console.log('Fetched sale items response:', fetchedSaleItemsResponse.data);
              
              if (fetchedSaleItemsResponse.data.success) {
                // Transform sale items to include medicine names
                const itemsWithNames = await Promise.all(
                  fetchedSaleItemsResponse.data.data.map(async (item: any) => {
                    try {
                      const branchMedicineResponse = await apiClient.get(`/branch-medicines/${item.attributes.branch_medicine}`);
                      const medicineName = branchMedicineResponse.data.data?.attributes?.medicine?.data?.attributes?.medicine_name || 'Unknown Medicine';
                      return {
                        medicine_name: medicineName,
                        quantity: item.attributes.quantity,
                        unit_price: item.attributes.unit_price,
                        total_price: item.attributes.total_price
                      };
                    } catch (error) {
                      console.error('Error fetching medicine name:', error);
                      return {
                        medicine_name: 'Unknown Medicine',
                        quantity: item.attributes.quantity,
                        unit_price: item.attributes.unit_price,
                        total_price: item.attributes.total_price
                      };
                    }
                  })
                );
                
                // Add items to the sale data for receipt
                response.data.data.sale_items = itemsWithNames;
              }
            } catch (error) {
              console.error('Error fetching sale items for receipt:', error);
            }
          } catch (saleItemsError: any) {
            console.error('Error creating sale items:', saleItemsError);
            console.error('Sale items error details:', saleItemsError.response?.data);
            message.warning('Sale created but items could not be saved. Please contact support.');
          }
        } else {
          console.log('No cart items found, skipping sale items creation');
          
          // Create sale payments if there are payment methods (even without cart items)
          if (paymentMethods.length > 0) {
            console.log('Creating sale payments for consultation-only sale:', paymentMethods);
            try {
              const salePaymentsData = paymentMethods.map(payment => ({
                sale: saleId,
                payment_method: payment.method,
                amount: payment.amount,
                  payment_date: getCurrentISTISO(),
                notes: ''
              }));
              
              console.log('Sale payments data:', salePaymentsData);
              
              const salePaymentsResponse = await apiClient.post('/sale-payments/bulk', {
                sale_payments: salePaymentsData
              });
              
              console.log('Sale payments creation response:', salePaymentsResponse.data);
              console.log('Sale payments created successfully');
            } catch (salePaymentsError: any) {
              console.error('Error creating sale payments:', salePaymentsError);
              console.error('Sale payments error details:', salePaymentsError.response?.data);
              message.warning('Sale completed but payment methods could not be saved. Please contact support.');
            }
          } else {
            console.log('No payment methods to create');
          }
        }
        
        // After saving, fetch the sale from server and compute values from table
        let finalSubtotal = medicineSubtotal;
        let finalDiscountAmount = medicineDiscountAmount;
        let finalDiscountPercentage = medicineDiscountPercentage;
        let finalTotalAmountFromServer = totalAmount;

        console.log('Initial values:', {
          medicineSubtotal,
          medicineDiscountAmount,
          medicineDiscountPercentage,
          totalAmount
        });

        try {
          const saleDetailsResponse = await apiClient.get(`/sales/${saleId}?populate[sale_items]=*&populate[branch]=*`);
          const saleNode = saleDetailsResponse.data?.data;
          const saleAttributes = saleNode?.attributes ? saleNode.attributes : saleNode; // Support both shapes

          console.log('Server sale data:', saleAttributes);

          // Prefer server-provided subtotal; if absent, compute from items
          const serverItemsRaw = saleAttributes?.sale_items?.data
            ? saleAttributes.sale_items.data.map((d: any) => d.attributes)
            : (response.data.data?.sale_items || []);

          const computedSubtotalFromItems = Array.isArray(serverItemsRaw)
            ? serverItemsRaw.reduce((sum: number, it: any) => sum + (parseFloat(it?.total_price) || 0), 0)
            : 0;

          const serverSubtotal = parseFloat(
            saleAttributes?.subtotal ?? saleAttributes?.sub_total ?? computedSubtotalFromItems
          );
          const serverDiscountAmount = parseFloat(saleAttributes?.discount_amount ?? `${finalDiscountAmount}`);
          const serverTotalAmount = parseFloat(saleAttributes?.total_amount ?? `${finalTotalAmountFromServer}`);

          console.log('Server values:', {
            serverSubtotal,
            serverDiscountAmount,
            serverTotalAmount,
            computedSubtotalFromItems
          });

          if (!Number.isNaN(serverSubtotal) && serverSubtotal > 0) {
            finalSubtotal = serverSubtotal;
          }
          if (!Number.isNaN(serverDiscountAmount)) {
            finalDiscountAmount = serverDiscountAmount;
          }
          if (!Number.isNaN(serverTotalAmount)) {
            finalTotalAmountFromServer = serverTotalAmount;
          }

          finalDiscountPercentage = finalSubtotal > 0
            ? (finalDiscountAmount / finalSubtotal) * 100
            : 0;

          console.log('Final values after server fetch:', {
            finalSubtotal,
            finalDiscountAmount,
            finalDiscountPercentage,
            finalTotalAmountFromServer
          });
        } catch (saleFetchError) {
          console.error('Error fetching sale details for receipt:', saleFetchError);
          console.log('Using fallback values:', {
            finalSubtotal,
            finalDiscountAmount,
            finalDiscountPercentage,
            finalTotalAmountFromServer
          });
        }

        console.log('Setting receipt data:', response.data.data);
        console.log('Sale items in receipt data:', response.data.data.sale_items);
        
        // Fetch branch details for receipt
        let branchDetails = {
          branch_name: ENV_CONFIG.DEFAULT_STORE_NAME,
          location: ENV_CONFIG.DEFAULT_LOCATION,
          phone: ENV_CONFIG.DEFAULT_CONTACT,
          email: ''
        };
        
        if (branchId) {
          try {
            const branchResponse = await apiClient.get(`/branches/${branchId}`);
            const branch = branchResponse.data.data;
            if (branch) {
              branchDetails = {
                branch_name: branch.attributes?.branch_name || branch.branch_name || ENV_CONFIG.DEFAULT_STORE_NAME,
                location: branch.attributes?.address || branch.address || ENV_CONFIG.DEFAULT_LOCATION,
                phone: branch.attributes?.phone || branch.phone || ENV_CONFIG.DEFAULT_CONTACT,
                email: branch.attributes?.email || branch.email || ''
              };
            }
          } catch (branchError) {
            console.error('Error fetching branch details:', branchError);
          }
        }
        
        // Ensure we have valid values for receipt
        const receiptSubtotal = finalSubtotal > 0 ? finalSubtotal : medicineSubtotal;
        const receiptDiscountAmount = finalDiscountAmount > 0 ? finalDiscountAmount : medicineDiscountAmount;
        const receiptDiscountPercentage = finalDiscountPercentage > 0 ? finalDiscountPercentage : medicineDiscountPercentage;
        const receiptTotalAmount = finalTotalAmountFromServer > 0 ? finalTotalAmountFromServer : totalAmount;

        console.log('Receipt values:', {
          receiptSubtotal,
          receiptDiscountAmount,
          receiptDiscountPercentage,
          receiptTotalAmount
        });

        // Ensure receipt data has sale_items for proper receipt generation
        const receiptData = {
          ...response.data.data,
          // Ensure all required fields exist with fallbacks
          receipt_number: response.data.data.receipt_number || receiptNumber,
          total_amount: response.data.data.total_amount || receiptTotalAmount,
          subtotal: receiptSubtotal, // Use server/table values when available
          discount_amount: receiptDiscountAmount, // Use server/table values when available
          discount_percentage: receiptDiscountPercentage,
          // consultation_fee: consultationFee, // Commented out - consultancy fee not needed
          consultation_fee: 0, // Set to 0 - consultancy fee disabled
          patient_name: response.data.data.patient_name || values.customer_name,
          patient_id: response.data.data.patient_id || values.customer_mobile,
          branch_name: branchDetails.branch_name,
          location: branchDetails.location,
          address: branchDetails.location,
          phone: branchDetails.phone,
          contact_info: branchDetails.phone,
          email: branchDetails.email,
          // Add payment methods for receipt display
          payment_methods: paymentMethods.map(payment => ({
            payment_method: payment.method,
            amount: payment.amount,
            payment_date: getCurrentISTISO()
          })),
          // Add sale_items from API or fallback to cart items
          sale_items: response.data.data.sale_items || cartItems.map(item => ({
            medicine_name: item.medicine.medicine_name,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            total_price: item.totalPrice,
            discount_percentage: 0
          }))
        };
        
        console.log('Final receipt data with sale_items:', receiptData);
        setReceiptData(receiptData);
        setReceiptModalVisible(true);
        console.log('Calling onClearCart - cart items will be cleared');
        onClearCart();
        setPaymentMethods([]); // Clear payment methods
        setMedicineDiscountPercentage(0); // Clear medicine discount percentage
        // setConsultationFee(0); // Commented out - consultancy fee not needed
        form.resetFields();
        message.success('Sale completed successfully!');
        // Call the success callback to refresh parent data
        if (onCheckoutSuccess) {
          onCheckoutSuccess();
        }
      } else {
        console.error('Sale creation condition not met:', response.data);
        console.error('Response status:', response.status);
        console.error('Data status:', response.data.status, 'Success:', response.data.success);
        console.error('Has data.id:', response.data.data && response.data.data.id);
        message.error('Failed to create sale');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Checkout failed';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    // Ensure logo is loaded before printing
    const printReceiptWithLogo = async () => {
      if (!logoBase64) {
        console.warn('Logo not loaded yet, waiting...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      console.log('Logo state before printing:', {
        hasLogoBase64: !!logoBase64,
        logoPreview: logoBase64?.substring(0, 50) + '...',
        currentOrigin: window.location.origin
      });
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const html = generateReceiptHTML();
        console.log('Generated HTML preview:', html.substring(0, 500));
        printWindow.document.write(html);
        printWindow.document.close();
        
        // Wait a bit for images to load before printing
        setTimeout(() => {
          printWindow.print();
        }, 200);
      }
    };
    printReceiptWithLogo();
  };

  const handleDownloadReceipt = () => {
    const receiptHTML = generateReceiptHTML();
    const blob = new Blob([receiptHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${receiptData?.receipt_number || 'unknown'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateReceiptHTML = () => {
    if (!receiptData) return '';

    console.log('Generating receipt HTML with data:', receiptData);
    console.log('Sale items in receipt data:', receiptData.sale_items);

    // The receiptData is the sale object directly, not nested under 'sale' property
    const sale = receiptData;
    const receipt_number = sale.receipt_number;
    
    // Add safety check for sale object
    if (!sale) {
      console.error('Sale object is undefined in receiptData:', receiptData);
      return '';
    }
    
    console.log('Generating receipt for sale:', sale);
    console.log('Sale items in receipt generation:', sale.sale_items);
    
    const currentDate = formatForReceipt(new Date());
    // Use base64 logo if available, otherwise fall back to URL
    // For print context, ensure we have the full path or base64
    const logoSrc = logoBase64 || (typeof window !== 'undefined' && window.location.origin ? `${window.location.origin}/Gosval.png` : '/Gosval.png');
    
    // Debug: Log logo status
    console.log('Logo loaded:', !!logoBase64, 'Fallback URL:', logoSrc);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${receipt_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start; 
            border-bottom: 2px solid #000; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header-left { 
            flex: 1; 
            text-align: left; 
            display: flex; 
            align-items: center; 
          }
          .logo {
            max-height: 60px;
            max-width: 120px;
            object-fit: contain;
          }
          .header-center { 
            flex: 2; 
            text-align: center; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center; 
          }
          .header-right { 
            flex: 1; 
            text-align: right; 
            display: flex; 
            flex-direction: column; 
            align-items: flex-end; 
          }
          .branch-name { 
            font-size: 22px; 
            font-weight: bold; 
            margin: 0; 
            margin-bottom: 5px; 
          }
          .branch-location { 
            font-size: 14px; 
            color: #666; 
            margin: 0; 
          }
          .contact-info { 
            font-size: 12px; 
            color: #333; 
            margin: 3px 0; 
          }
          .email-link { 
            color: #1890ff; 
            text-decoration: none; 
          }
          .receipt-info { margin-bottom: 20px; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .items-table th, .items-table td { border: 1px solid #000; padding: 8px; text-align: left; }
          .items-table th { background-color: #f0f0f0; }
          .total-section { text-align: right; margin-top: 20px; }
          .footer { text-align: center; margin-top: 30px; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${logoSrc}" alt="Logo" class="logo" />
          </div>
          
          <div class="header-center">
            <p class="branch-name">${sale.branch_name || ENV_CONFIG.DEFAULT_STORE_NAME}</p>
            <p class="branch-location">${sale.location || sale.address || ENV_CONFIG.DEFAULT_LOCATION}</p>
          </div>
          
          <div class="header-right">
            <p class="contact-info" style="font-weight: bold;">Contact Information</p>
            ${sale.contact_info || sale.phone ? `<p class="contact-info">Phone: ${sale.contact_info || sale.phone || ENV_CONFIG.DEFAULT_CONTACT}</p>` : ''}
            ${sale.email ? `<p class="contact-info">Email: <a href="mailto:${sale.email}" class="email-link">${sale.email}</a></p>` : ''}
          </div>
        </div>
        
        <div class="receipt-info">
          <p><strong>Receipt Number:</strong> ${receipt_number}</p>
          <p><strong>Date:</strong> ${currentDate}</p>
          <p><strong>Customer:</strong> ${sale.patient_name || sale.customer_name || 'Walk-in Customer'}</p>
          ${(sale.patient_id || sale.customer_mobile) ? `<p><strong>Mobile:</strong> ${sale.patient_id || sale.customer_mobile}</p>` : ''}
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Medicine</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${(sale.sale_items || []).map((item: any) => {
              console.log('Processing sale item for receipt:', item);
              return `
                <tr>
                  <td>${item.medicine_name || 'Unknown Medicine'}</td>
                  <td>${item.quantity || 0}</td>
                  <td>₹${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                  <td>₹${parseFloat(item.total_price || 0).toFixed(2)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="total-section">
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Medicine Subtotal:</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>₹${parseFloat(sale.subtotal || 0).toFixed(2)}</strong></td>
            </tr>
            ${sale.discount_amount && sale.discount_amount > 0 ? `
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Medicine Discount (${parseFloat(sale.discount_percentage || 0).toFixed(1)}%):</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>-₹${parseFloat(sale.discount_amount).toFixed(2)}</strong></td>
            </tr>
            ` : ''}
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Medicine Total:</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>₹${(parseFloat(sale.subtotal || 0) - parseFloat(sale.discount_amount || 0)).toFixed(2)}</strong></td>
            </tr>
            ${/* Consultation fee row commented out - not needed for now
            sale.consultation_fee && sale.consultation_fee > 0 ? `
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Consultation Fee:</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>₹${parseFloat(sale.consultation_fee).toFixed(2)}</strong></td>
            </tr>
            ` : ''*/ ''}
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 12px 0; text-align: left; font-size: 16px;"><strong>TOTAL AMOUNT:</strong></td>
              <td style="padding: 12px 0; text-align: right; font-size: 16px;"><strong>₹${parseFloat(sale.total_amount || 0).toFixed(2)}</strong></td>
            </tr>
          </table>
        </div>
        
        ${sale.payment_methods && sale.payment_methods.length > 0 ? `
        <div class="payment-section">
          <h3>Payment Details</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${sale.payment_methods.map((payment: any) => `
                <tr>
                  <td>${payment.payment_method ? payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1) : 'Cash'}</td>
                  <td>₹${parseFloat(payment.amount || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>Thank you for your purchase!</p>
          <p>Please keep this receipt for your records.</p>
        </div>
      </body>
      </html>
    `;
  };

  const cartColumns = [
    {
      title: 'Medicine',
      dataIndex: 'medicine',
      key: 'medicine',
      render: (medicine: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{medicine.medicine_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {medicine.manufacturer}
          </div>
        </div>
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (price: number) => `₹${(price || 0).toFixed(2)}`,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number, record: CartItem) => (
        <Space>
          <Button
            size="small"
            icon={<MinusOutlined />}
            onClick={() => updateQuantity(record.medicine.branch_medicine_id as number, quantity - 1)}
          />
          <InputNumber
            size="small"
            min={1}
            value={quantity}
            onChange={(value) => updateQuantity(record.medicine.branch_medicine_id as number, value || 1)}
            style={{ width: '60px' }}
          />
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => updateQuantity(record.medicine.branch_medicine_id as number, quantity + 1)}
          />
        </Space>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (price: number) => `₹${(price || 0).toFixed(2)}`,
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: CartItem) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.medicine.branch_medicine_id as number)}
        />
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Title level={4}>
          <ShoppingCartOutlined /> Checkout
        </Title>

        {cartItems.length === 0 ? (
          <Alert
            message="Cart is Empty"
            description="Add medicines to cart to proceed with checkout"
            type="info"
            showIcon
          />
        ) : (
          <>
            <Table
              columns={cartColumns}
              dataSource={Array.isArray(cartItems) ? cartItems : []}
              pagination={false}
              size="small"
      rowKey={(item) => item.medicine.branch_medicine_id as any}
            />

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Subtotal"
                  value={calculateSubtotal()}
                  prefix="₹"
                  precision={2}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total"
                  value={calculateTotal()}
                  prefix="₹"
                  precision={2}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
            </Row>

            <Divider />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleCheckout}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0 }}>Customer Information</Title>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => setCreatePatientModalVisible(true)}
                >
                  Create New Patient
                </Button>
              </div>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="customer_name"
                    label="Customer Name"
                    rules={[{ required: true, message: 'Please enter customer name' }]}
                  >
                    <AutoComplete
                      options={patientOptions}
                      onSearch={debouncedSearchByName}
                      onSelect={handlePatientSelect}
                      notFoundContent={searchingPatients ? 'Searching...' : 'No patients found'}
                      filterOption={false}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="Type to search patients or enter new name"
                      />
                    </AutoComplete>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="customer_mobile"
                    label="Mobile Number"
                    rules={[
                      { required: true, message: 'Please enter mobile number' },
                      { 
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (value.length < 10) {
                            return Promise.reject('Mobile number must be at least 10 digits');
                          }
                          if (!/^[0-9]+$/.test(value)) {
                            return Promise.reject('Mobile number must contain only digits');
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <AutoComplete
                      options={patientOptions}
                      onSearch={debouncedSearchByPhone}
                      onSelect={handlePatientSelect}
                      notFoundContent={searchingPatients ? 'Searching...' : 'No patients found'}
                      filterOption={false}
                    >
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder="Type to search by phone or enter new number"
                      />
                    </AutoComplete>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="notes"
                label="Notes (Optional)"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </Form.Item>

              <Divider>Medicine & Consultation</Divider>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Medicine Discount (%)"
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      value={medicineDiscountPercentage}
                      onChange={(value) => setMedicineDiscountPercentage(value || 0)}
                      min={0}
                      max={100}
                      style={{ width: '100%' }}
                      placeholder="Enter discount %"
                      formatter={(value) => `${value}%`}
                      parser={(value) => parseFloat(value!.replace('%', ''))}
                    />
                  </Form.Item>
                </Col>
                {/* Consultation fee input commented out - not needed for now */}
                {/* <Col span={12}>
                  <Form.Item
                    label="Consultation Fee (₹)"
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      value={consultationFee}
                      onChange={(value) => setConsultationFee(value || 0)}
                      min={0}
                      style={{ width: '100%' }}
                      placeholder="Enter consultation fee"
                      formatter={(value) => `₹${value}`}
                      parser={(value) => parseFloat(value!.replace('₹', ''))}
                    />
                  </Form.Item>
                </Col> */}
              </Row>

              <Divider>Payment Methods</Divider>
              
              {/* Payment Summary Table */}
              <div style={{ marginBottom: 16 }}>
                <Table
                  dataSource={[
                    {
                      key: 'medicine_subtotal',
                      description: 'Medicine Subtotal',
                      amount: calculateSubtotal()
                    },
                    {
                      key: 'medicine_discount',
                      description: 'Medicine Discount',
                      amount: -calculateMedicineDiscountAmount()
                    },
                    // Consultation fee row commented out - not needed for now
                    // {
                    //   key: 'consultation_fee',
                    //   description: 'Consultation Fee',
                    //   amount: consultationFee
                    // },
                    {
                      key: 'total_amount',
                      description: 'Total Amount',
                      amount: calculateTotal(),
                      isTotal: true
                    },
                    {
                      key: 'amount_paid',
                      description: 'Amount Paid',
                      amount: getTotalPaid()
                    },
                    {
                      key: 'remaining',
                      description: getRemainingAmount() > 0 ? 'Remaining (Underpaid)' : 
                                 getRemainingAmount() < 0 ? 'Change Due (Overpaid)' : 'Remaining',
                      amount: getRemainingAmount(),
                      isRemaining: true
                    }
                  ]}
                  columns={[
                    {
                      title: 'Description',
                      dataIndex: 'description',
                      key: 'description',
                      render: (text: string, record: any) => (
                        <span style={{ 
                          fontWeight: record.isTotal ? 'bold' : 'normal',
                          fontSize: record.isTotal ? '16px' : '14px'
                        }}>
                          {text}
                        </span>
                      )
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right',
                      render: (amount: number, record: any) => (
                        <span style={{ 
                          fontWeight: record.isTotal ? 'bold' : 'normal',
                          fontSize: record.isTotal ? '16px' : '14px',
                          color: record.isRemaining 
                            ? (amount > 0 ? '#ff4d4f' : '#52c41a')
                            : record.isTotal 
                              ? '#1890ff' 
                              : 'inherit'
                        }}>
                          {amount >= 0 ? '₹' : '-₹'}{Math.abs(amount).toFixed(2)}
                        </span>
                      )
                    }
                  ]}
                  pagination={false}
                  size="small"
                  showHeader={true}
                  bordered={false}
                  style={{ 
                    backgroundColor: '#fafafa',
                    borderRadius: '6px'
                  }}
                />
              </div>

              {/* Payment Methods List */}
              {paymentMethods.map((payment, index) => (
                <Card 
                  key={payment.id} 
                  size="small" 
                  style={{ marginBottom: 8 }}
                  title={
                    <Space>
                      {payment.method === 'cash' && <DollarOutlined />}
                      {payment.method === 'card' && <CreditCardOutlined />}
                      {payment.method === 'upi' && <GlobalOutlined />}
                      Payment Method {index + 1}
                    </Space>
                  }
                  extra={
                    <Button 
                      type="text" 
                      danger 
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removePaymentMethod(payment.id)}
                    />
                  }
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Select
                        value={payment.method}
                        onChange={(value) => updatePaymentMethod(payment.id, 'method', value)}
                        style={{ width: '100%' }}
                      >
                        <Select.Option value="cash">
                          <Space>
                            <DollarOutlined />
                            Cash
                          </Space>
                        </Select.Option>
                        <Select.Option value="card">
                          <Space>
                            <CreditCardOutlined />
                            Card
                          </Space>
                        </Select.Option>
                        <Select.Option value="upi">
                          <Space>
                            <GlobalOutlined />
                            Online
                          </Space>
                        </Select.Option>
                      </Select>
                    </Col>
                    <Col span={12}>
                      <InputNumber
                        value={payment.amount}
                        onChange={(value) => updatePaymentMethod(payment.id, 'amount', value || 0)}
                        placeholder="Amount"
                        min={0}
                        max={calculateTotal()}
                        precision={2}
                        style={{ width: '100%' }}
                        addonBefore="₹"
                      />
                    </Col>
                  </Row>
                </Card>
              ))}

              {/* Add Payment Method Button */}
              <Button
                type="dashed"
                onClick={addPaymentMethod}
                icon={<PlusCircleOutlined />}
                style={{ width: '100%', marginBottom: 16 }}
              >
                Add Payment Method
              </Button>

              {/* Payment Status Alert */}
              {paymentMethods.length > 0 && (
                <Alert
                  message={
                    isPaymentComplete() 
                      ? "Payment Complete" 
                      : `Payment Incomplete - Remaining: ₹${getRemainingAmount().toFixed(2)}`
                  }
                  type={isPaymentComplete() ? "success" : "warning"}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                >
                  Process Payment
                </Button>
                <Button
                  onClick={onClearCart}
                  icon={<DeleteOutlined />}
                >
                  Clear Cart
                </Button>
              </Space>
            </Form>
          </>
        )}
      </Card>

      {/* Receipt Modal */}
      <Modal
        title="Receipt Generated"
        open={receiptModalVisible}
        onCancel={() => setReceiptModalVisible(false)}
        footer={[
          <Button key="print" type="primary" icon={<FileTextOutlined />} onClick={handlePrintReceipt}>
            Print Receipt
          </Button>,
          <Button key="download" onClick={handleDownloadReceipt}>
            Download Receipt
          </Button>,
          <Button key="close" onClick={() => setReceiptModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {receiptData && (
          <div>
            <Alert
              message="Sale Completed Successfully!"
              description={`Receipt Number: ${receiptData.receipt_number}`}
              type="success"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            <p>You can now print or download the receipt for the customer.</p>
          </div>
        )}
      </Modal>

      {/* Create Patient Modal */}
      <Modal
        title="Create New Patient"
        open={createPatientModalVisible}
        onCancel={() => {
          setCreatePatientModalVisible(false);
          createPatientForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createPatientForm}
          layout="vertical"
          onFinish={handleCreatePatient}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="name"
              label="Patient Name"
              rules={[
                { required: true, message: 'Please enter patient name' },
                { min: 2, message: 'Name must be at least 2 characters' }
              ]}
            >
              <Input placeholder="Enter patient full name" />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label="Phone Number"
              rules={[
                { required: true, message: 'Please enter phone number' },
                { pattern: /^[\d+\-\s()]{10,15}$/, message: 'Please enter a valid phone number' }
              ]}
            >
              <Input placeholder="e.g., +91 98765 43210 or 9876543210" />
            </Form.Item>
          </div>

          <Divider>Optional Information</Divider>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="email"
              label="Email"
            >
              <Input type="email" placeholder="example@email.com" />
            </Form.Item>

            <Form.Item
              name="age"
              label="Age"
            >
              <InputNumber
                placeholder="Enter age"
                min={0}
                max={150}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="gender"
              label="Gender"
            >
              <Select placeholder="Select gender">
                <Select.Option value="male">Male</Select.Option>
                <Select.Option value="female">Female</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="blood_group"
              label="Blood Group"
            >
              <Select placeholder="Select blood group">
                <Select.Option value="A+">A+</Select.Option>
                <Select.Option value="A-">A-</Select.Option>
                <Select.Option value="B+">B+</Select.Option>
                <Select.Option value="B-">B-</Select.Option>
                <Select.Option value="AB+">AB+</Select.Option>
                <Select.Option value="AB-">AB-</Select.Option>
                <Select.Option value="O+">O+</Select.Option>
                <Select.Option value="O-">O-</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="current_location"
            label="Current Location"
          >
            <Input placeholder="Enter location" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create Patient
              </Button>
              <Button onClick={() => setCreatePatientModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CheckoutForm;
